/**
 * useDealImportPreview — preview server-side (até 50 itens) de um job de
 * importação de negócios. Telefone e e-mail mascarados no banco.
 *
 * RELEASE:
 *   2026-05-17 v1.1.0 - Expande tipagem com email_masked, title, value,
 *                       currency, phase_name, source, tags, error_message.
 *   2026-05-17 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useDealImportPreview');

export interface DealImportPreviewItem {
  id: string;
  row_index: number;
  name: string | null;
  email_masked: string | null;
  phone_masked: string | null;
  title: string | null;
  value: number | null;
  currency: string | null;
  pipeline_name: string | null;
  phase_name: string | null;
  seller_name: string | null;
  source: string | null;
  tags: string | null;
  status: string;
  validation_errors: unknown;
  error_message: string | null;
}

export function useDealImportPreview(jobId: string | null, enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['deal-import-preview', currentTenant?.id, jobId],
    enabled: enabled && !!jobId && !!currentTenant?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<DealImportPreviewItem[]> => {
      const { data, error } = await (supabase.rpc as any)('get_deal_import_preview', {
        p_job_id: jobId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar preview de importação de negócios', error);
        throw error;
      }
      return (data ?? []) as DealImportPreviewItem[];
    },
  });
}
